import { call, put, takeEvery, select } from "redux-saga/effects";

import {
    GET_BILLING_CARDS,
    BILLING_CARD_ADD,
    BILLING_ADDRESSES_GET,
    BILLING_ADDRESSES_ADD,
    BILLING_INFO_GET,
    BILLING_INFO_PLANS_GET,
    BILLING_INFO_PLANS_SET,
    BILLING_CARD_SET,
    BILLING_CARD_DELETE,
    BILLING_CARD_EDIT,
    BILLING_ADDRESSES_DELETE,
    updateBillingInfo,
    BILLING_ADDRESSES_EDIT,
    BILLING_ADDRESSES_SET_ADDRESS,
    BILLING_INFO_USAGE_GET,
    BILLING_INFO_UPDATE_DATA,
    BILLING_PLAN_CURRENT_GET,
    BILLING_STATEMENT_PAY,
    BILLING_SMS_PACKAGES_GET,
    BILLING_SMS_PACKAGE_PURCHASE,
    BILLING_SMS_PURCHASES_GET,
    BILLING_SMS_BALANCE_GET,
    BILLING_USAGE_CURRENT_GET,
    deleteBillingPaymentAC, addNewBillingPayment, setBillingPayments,
} from "../reducers/billing/billing-actions";
import { actionAccountTopNotification } from "../reducers/notification/notification-actions";
import {
    addBillingAddress,
    deleteBillingAddress,
    getBillingAddresses,
    getBillingInfo,
    getBillingPlans,
    setAccountPlan,
    getAccountCards,
    billingCardAdd,
    billingCardDelete,
    billingCardEdit,
    editBillingAddress,
    setBillingAddress,
    getBillingUsages, updateAccountCard,
    getCurrentPlan,
    payStatement,
    getSmsPackages,
    purchaseSmsPackage,
    getSmsPurchases,
    getSmsBalances,
    getBillingUsage
} from "../../api/billing-request";

import BillingService from "../../services/billing";

import { getErrorMessage, requestResultIsOk } from "../../utils/request-util";
import { objectKeysToUpperLowerCase } from "../../utils/objects-util";
import { createSaga, ErrorData } from "../../utils/sagaHelper";

import { getBilling } from "../selectors";
import {getInitModel} from "../../services/initModels.js";
import {MODEL_TYPE} from "../../utils/enums.js";

// The portal shows the most recent statements only (the server caps the list the same way).
const RECENT_STATEMENTS_LIMIT = 12;

// Merges a statement returned by a payment/purchase endpoint into the invoices table in place:
// replaces the row when it is already there, otherwise puts the new one on top (newest first).
// Nothing is re-fetched — the response is the source of truth.
function* mergeStatement(statement) {
    if(!statement || !statement.id) return;

    const billing = yield select(getBilling);
    const invoices = billing.invoices || [];
    const exists = invoices.some(invoice => invoice.id === statement.id);

    const merged = exists
        ? invoices.map(invoice => invoice.id === statement.id ? statement : invoice)
        : [statement, ...invoices].slice(0, RECENT_STATEMENTS_LIMIT);

    yield put(updateBillingInfo({ invoices: merged }));
}

function* getBillingInfoSaga(action) {
    yield* createSaga(function*() {
        const requestResult = yield call(getBillingInfo);
        if(requestResultIsOk(requestResult)) {
            yield put(updateBillingInfo(objectKeysToUpperLowerCase(requestResult.data)));
        } else {
            return new ErrorData(getErrorMessage(requestResult));
        }
    }, action.type);
}

function* billingPlansGetSaga(action) {
    yield* createSaga(function*() {
        const billing = yield select(getBilling);
        if(!billing || !billing.info) {
            yield put({ type: BILLING_INFO_GET });
        }

        const requestResult = yield call(getBillingPlans);
        if(requestResultIsOk(requestResult)) {            
            yield put(updateBillingInfo({ plans: objectKeysToUpperLowerCase(requestResult.data) }));
        } else {
            return new ErrorData(getErrorMessage(requestResult));
        }
    }, action.type);
}

function* billingPlanSetSaga(action) {
    yield* createSaga(function*() {
        const requestResult = yield call(setAccountPlan, action.data);
        if(requestResultIsOk(requestResult)) {
            // An upgrade charges a prorated delta right away and returns its statement —
            // add that row without waiting for a re-fetch.
            const payload = objectKeysToUpperLowerCase(requestResult.data) || {};
            yield* mergeStatement(payload.statement);

            yield put({ type: BILLING_PLAN_CURRENT_GET });
        } else {
            return new ErrorData(getErrorMessage(requestResult));
        }
    }, action.type);
}

function* billingCurrentPlanGetSaga(action) {
    yield* createSaga(function*() {
        const requestResult = yield call(getCurrentPlan);
        if(requestResultIsOk(requestResult)) {
            yield put(updateBillingInfo({ currentPlan: objectKeysToUpperLowerCase(requestResult.data) }));
        } else {
            return new ErrorData(getErrorMessage(requestResult));
        }
    }, action.type);
}

function* statementPaySaga(action) {
    yield* createSaga(function*() {
        const { statementId, walletToken, cardId, intentToken, bankDebit, bankTransferTransactionId } = typeof action.data === 'string'
            ? { statementId: action.data }
            : (action.data || {});

        let requestResult;
        try {
            requestResult = yield call(payStatement, statementId, walletToken, cardId, intentToken, bankDebit, bankTransferTransactionId);
        } catch (error) {
            // The api rejects with the raw response on 4xx — without this catch the throw died in
            // createSaga's console.log and the FAIL action (with the provider's refusal text in
            // data.Message) never reached the component's toast.
            return new ErrorData(getErrorMessage({ response: error }));
        }
        if(requestResultIsOk(requestResult)) {
            // The endpoint returns the updated statement (and whether the account is settled),
            // so the invoices table, the plan tile and the warning banner update in place —
            // no re-fetch, no page reload.
            const payload = objectKeysToUpperLowerCase(requestResult.data) || {};

            yield* mergeStatement(payload.statement);

            const billing = yield select(getBilling);

            if(payload.accountRestored) {
                yield put(actionAccountTopNotification(null));

                if(billing.currentPlan) {
                    yield put(updateBillingInfo({
                        currentPlan: { ...billing.currentPlan, subscriptionState: 0 }
                    }));
                }
            }
        } else {
            return new ErrorData(getErrorMessage(requestResult));
        }
    }, action.type);
}

function* smsPackagesGetSaga(action) {
    yield* createSaga(function*() {
        const requestResult = yield call(getSmsPackages);
        if(requestResultIsOk(requestResult)) {
            // { packages, otpAvailable, campaignsAvailable } — packages are UniCell-only and each
            // purpose is purchasable only when its provider user is configured.
            const payload = objectKeysToUpperLowerCase(requestResult.data) || {};
            yield put(updateBillingInfo({
                smsPackages: payload.packages || [],
                smsAvailability: {
                    otp: !!payload.otpAvailable,
                    campaigns: !!payload.campaignsAvailable
                },
                // Per-purpose one-time bank transfer offer ({ terminal, totals: [{code, total}] }
                // or null) — the purposes may charge through different billing profiles, so each
                // has its own terminal and its own server-computed gross totals.
                smsBankTransfer: {
                    otp: (payload.bankTransfer && payload.bankTransfer.otp) || null,
                    campaigns: (payload.bankTransfer && payload.bankTransfer.campaigns) || null
                }
            }));
        } else {
            return new ErrorData(getErrorMessage(requestResult));
        }
    }, action.type);
}

function* smsPackagePurchaseSaga(action) {
    yield* createSaga(function*() {
        const { packageCode, walletToken, cardId, intentToken, purpose, bankTransferTransactionId } = typeof action.data === 'string'
            ? { packageCode: action.data }
            : (action.data || {});
        const requestResult = yield call(purchaseSmsPackage, packageCode, walletToken, cardId, intentToken, purpose, bankTransferTransactionId);
        if(requestResultIsOk(requestResult)) {
            // The purchase creates its own statement — show it in the invoices table right away.
            const payload = objectKeysToUpperLowerCase(requestResult.data) || {};
            yield* mergeStatement(payload.statement);
        } else {
            return new ErrorData(getErrorMessage(requestResult));
        }
    }, action.type);
}

function* smsPurchasesGetSaga(action) {
    yield* createSaga(function*() {
        const requestResult = yield call(getSmsPurchases);
        if(requestResultIsOk(requestResult)) {
            yield put(updateBillingInfo({ smsPurchases: objectKeysToUpperLowerCase(requestResult.data) }));
        } else {
            return new ErrorData(getErrorMessage(requestResult));
        }
    }, action.type);
}

function* smsBalanceGetSaga(action) {
    yield* createSaga(function*() {
        // Live UniCell balances per purpose user; failure just means "nothing to show".
        const requestResult = yield call(getSmsBalances);
        if(requestResultIsOk(requestResult)) {
            const payload = objectKeysToUpperLowerCase(requestResult.data) || {};
            yield put(updateBillingInfo({ smsBalances: { otp: payload.otp ?? null, campaigns: payload.campaigns ?? null } }));
        } else {
            yield put(updateBillingInfo({ smsBalances: null }));
        }
    }, action.type);
}

function* billingUsageGetSaga(action) {
    yield* createSaga(function*() {
        const requestResult = yield call(getBillingUsage);
        if(requestResultIsOk(requestResult)) {
            yield put(updateBillingInfo({ currentUsage: objectKeysToUpperLowerCase(requestResult.data) }));
        } else {
            return new ErrorData(getErrorMessage(requestResult));
        }
    }, action.type);
}

function* getBillingCardsSaga(action) {
    yield* createSaga(function*() {
        const requestResult = yield call(getAccountCards);
        if(requestResultIsOk(requestResult)) {
            // The internal billing/card endpoint returns a LIST of cards (the legacy
            // Recurly one returned a single object — do not re-wrap an array).
            const data = requestResult.data;
            yield put(setBillingPayments(Array.isArray(data) ? data : [data].filter(Boolean)));
        } else {
            return new ErrorData(getErrorMessage(requestResult));
        }
    }, action.type);
}

function* billingCardSetSaga(action) {
    yield* createSaga(function*() {
        const requestResult = yield call(updateAccountCard, action.data.id);
        if(requestResultIsOk(requestResult)) {
            const creditCards = yield select(state => state.billing.creditCards);
            yield put(setBillingPayments(
                creditCards.map(card => ({ ...card, default: card.id === action.data.id }))
            ));
        } else {
            return new ErrorData(getErrorMessage(requestResult));
        }
    }, action.type);
}

function* billingCardDeleteSaga(action) {
    yield* createSaga(function*() {
        const requestResult = yield call(billingCardDelete, action.data);
        if(requestResultIsOk(requestResult)) {
            yield put(deleteBillingPaymentAC({ id: action.data }));
        } else {
            return new ErrorData(getErrorMessage(requestResult)); 
        }
    }, action.type);
}

function* addBillingCardSaga(action) {
    yield* createSaga(function*() {
        delete action.data.id;
        action.data.default = true;
        // Normalize to MM/YYYY. The old condition was inverted — a value that ALREADY had a
        // slash got a second one inserted ("10/2031" → "10//2031").
        if(!action.data.expiry.includes('/')) {
            action.data.expiry = action.data.expiry.slice(0, 2) + '/' + action.data.expiry.slice(2);
        }
        const card = getInitModel(MODEL_TYPE.creditCard, action.data);
        const requestResult = yield call(billingCardAdd, card);
        if(requestResultIsOk(requestResult)) {
            yield put(addNewBillingPayment(requestResult.data));
            return;
        } else {
            return new ErrorData(getErrorMessage(requestResult));
        }
    }, action.type);
}

function* editBillingCardSaga(action) {
    yield* createSaga(function*() {
        const { tokenId, billingInfoId } = action.data;
        const requestResult = yield call(billingCardEdit, billingInfoId, tokenId);
        if(requestResultIsOk(requestResult)) {
            yield put(updateBillingInfo({ cards: objectKeysToUpperLowerCase(requestResult.data || []) }));
        } else {
            return new ErrorData(getErrorMessage(requestResult)); 
        }
    }, action.type);
}

// Server billing details ({contactName, email, phone, company, country, ...}) → the shape the
// address form edits (billingAddressModel).
function toAddressForm(details) {
    const d = details || {};
    const nameParts = (d.contactName || "").trim().split(/\s+/);

    return {
        firstName: nameParts[0] || "",
        lastName: nameParts.slice(1).join(" "),
        company: d.company || "",
        email: d.email || "",
        phone: d.phone || "",
        address: {
            country: d.country || "",
            city: d.city || "",
            postalCode: d.postal || "",
            street1: d.street1 || "",
            street2: d.street2 || "",
            region: d.region || ""
        }
    };
}

function* getBillingAddressesSaga(action) {
    yield* createSaga(function*() {
        const requestResult = yield call(getBillingAddresses);
        if(requestResultIsOk(requestResult)) {
            const details = objectKeysToUpperLowerCase(requestResult.data) || {};
            yield put(updateBillingInfo({ billingDetailsForm: toAddressForm(details) }));
        } else {
            return new ErrorData(getErrorMessage(requestResult));
        }
    }, action.type);
}

function* deleteBillingAddressSaga(action) {
    yield* createSaga(function*() {
        const requestResult = yield call(deleteBillingAddress, action.data);
        if(requestResultIsOk(requestResult)) {
            yield put(updateBillingInfo({ shipmentAddresses: objectKeysToUpperLowerCase(requestResult.data || []) }));
        } else {
            return new ErrorData(getErrorMessage(requestResult)); 
        }
    }, action.type);
}

function* billingAddressSetSaga(action) {
    yield* createSaga(function*() {
        const requestResult = yield call(setBillingAddress, action.data);
        if(requestResultIsOk(requestResult)) {
            yield put(updateBillingInfo({ shipmentAddresses: objectKeysToUpperLowerCase(requestResult.data || []) }));
        } else {
            return new ErrorData(getErrorMessage(requestResult)); 
        }
    }, action.type);
}

function* addBillingAddressSaga(action) {
    yield* createSaga(function*() {
        // Form (billingAddressModel) → the server's flat billing-details model.
        const form = action.data || {};
        const address = form.address || {};

        const payload = {
            contactName: `${form.firstName || ""} ${form.lastName || ""}`.trim(),
            email: form.email,
            phone: form.phone,
            company: form.company,
            country: address.country,
            city: address.city,
            region: address.region,
            postal: address.postalCode,
            street1: address.street1,
            street2: address.street2
        };

        const requestResult = yield call(addBillingAddress, payload);
        if(requestResultIsOk(requestResult)) {
            // What was saved is what the form now shows — no refetch needed.
            yield put(updateBillingInfo({ billingDetailsForm: form }));
        } else {
            return new ErrorData(getErrorMessage(requestResult));
        }
    }, action.type);
}

function* editBillingAddressSaga(action) {
    yield* createSaga(function*() {
        const requestResult = yield call(editBillingAddress, action.data.id, action.data);
        if(requestResultIsOk(requestResult)) {
            yield put(updateBillingInfo({ shipmentAddresses: objectKeysToUpperLowerCase(requestResult.data || []) }));
        } else {
            return new ErrorData(getErrorMessage(requestResult)); 
        }
    }, action.type);
}

function* billingUsagesGetSaga(action) {
    yield* createSaga(function*() {
        const requestResult = yield call(getBillingUsages);
        if(requestResultIsOk(requestResult)) {
            yield put(updateBillingInfo({ usages: objectKeysToUpperLowerCase(requestResult.data) }));
        } else {
            return new ErrorData(getErrorMessage(requestResult));
        }
    }, action.type);
}

export function* updateBillingInfoDataSaga(action) {
    yield* createSaga(function*() {
        yield put(updateBillingInfo({ info: objectKeysToUpperLowerCase(action.data) }));
        const billing = yield select(getBilling);
        const topNotificationData = BillingService.instance.getAccountTopNotificationData(billing);
        yield put(actionAccountTopNotification(topNotificationData));
    }, action.type);
}

export default function* () {
    yield takeEvery(BILLING_INFO_GET, getBillingInfoSaga);
    yield takeEvery(BILLING_INFO_PLANS_GET, billingPlansGetSaga);
    yield takeEvery(BILLING_INFO_PLANS_SET, billingPlanSetSaga);
    yield takeEvery(BILLING_PLAN_CURRENT_GET, billingCurrentPlanGetSaga);
    yield takeEvery(BILLING_STATEMENT_PAY, statementPaySaga);
    yield takeEvery(BILLING_SMS_PACKAGES_GET, smsPackagesGetSaga);
    yield takeEvery(BILLING_SMS_PACKAGE_PURCHASE, smsPackagePurchaseSaga);
    yield takeEvery(BILLING_SMS_PURCHASES_GET, smsPurchasesGetSaga);
    yield takeEvery(BILLING_SMS_BALANCE_GET, smsBalanceGetSaga);
    yield takeEvery(BILLING_USAGE_CURRENT_GET, billingUsageGetSaga);
    yield takeEvery(GET_BILLING_CARDS, getBillingCardsSaga);
    yield takeEvery(BILLING_CARD_ADD, addBillingCardSaga);
    yield takeEvery(BILLING_CARD_EDIT, editBillingCardSaga);    
    yield takeEvery(BILLING_CARD_SET, billingCardSetSaga); 
    yield takeEvery(BILLING_CARD_DELETE, billingCardDeleteSaga);
    yield takeEvery(BILLING_ADDRESSES_GET, getBillingAddressesSaga);
    yield takeEvery(BILLING_ADDRESSES_ADD, addBillingAddressSaga);   
    yield takeEvery(BILLING_ADDRESSES_EDIT, editBillingAddressSaga);    
    yield takeEvery(BILLING_ADDRESSES_DELETE, deleteBillingAddressSaga);
    yield takeEvery(BILLING_ADDRESSES_SET_ADDRESS, billingAddressSetSaga);    
    yield takeEvery(BILLING_INFO_USAGE_GET, billingUsagesGetSaga);
    yield takeEvery(BILLING_INFO_UPDATE_DATA, updateBillingInfoDataSaga);
}